import React, { useCallback, useEffect, useState } from 'react';
import {
  ButtonNext,
  ButtonPrevious,
  Container,
  ContainerGutter,
  CTA,
  styled,
  Text,
  TextTag
} from '@volkswagen-onehub/components-core';
import useVehiclesQuery from '../api/use-vehicles-query';
import { Meta, Pagination as Page, ResponseSelector, VehiclesResponse } from '../types';
import { useAppliedFilterStore } from '../store/filtersv2';
import { PaginationSkeleton } from './PaginationSkeleton';
import { getTranslations } from '../translations';

const CurrentPageContainer = styled.div``;

export const Pagination = () => {

  const { pagination: labels } = getTranslations();

  const [pagination, setPagination] = useState<Page | null>(null);
  const [rangeStart, setRangeStart] = useState<number | null>(null);
  const [rangeEnd, setRangeEnd] = useState<number | null>(null);

  const getMetaData: ResponseSelector<VehiclesResponse, Meta> = res => res?.meta;
  const { isLoading, data } = useVehiclesQuery<VehiclesResponse, Meta>({ select: getMetaData });

  const { applied, applyFilter } = useAppliedFilterStore();

  const onJumpPage = useCallback((pageNum : string | number | null) => {
    if (pageNum) {
      applyFilter("page", pageNum);
    }
  }, [pagination]);

  const onNextPage = useCallback(async () => {
    if (!pagination) return;
    applyFilter("page", Number(applied?.page)+1);
  }, [pagination, applied]);

  const onPrevPage = useCallback(async () => {
    if (!pagination) return;
    applyFilter("page", Number(applied?.page)-1);
  }, [pagination, applied]);

  useEffect(() => {
    if(!pagination) return;

      const rangeStartMath = pagination.current_page - 2 < 1 ? 1 : pagination.current_page - 2;
      const rangeEndMath = pagination.current_page + 2 > pagination.total_pages ? pagination.total_pages : pagination.current_page + 2;

      setRangeStart(rangeStartMath);
      setRangeEnd(rangeEndMath);

  }, [pagination]);

  useEffect(() => {
    if ((data as Meta)?.pagination) {
      setPagination((data as Meta)?.pagination);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      setPagination(null);
    }
  }, []);

  const renderPagination = useCallback(() => {
    if(pagination == null || rangeEnd == null || rangeStart == null) return;

    let pages = [];

    pages.push(<ButtonPrevious key="prev" tag="button" size="small" disabled={pagination.current_page === 1} onClick={onPrevPage} ariaLabel={labels.prev} />);

    if (rangeStart <= 3) {
      for (let i = 1; i < rangeStart; i++) {
        if (i === pagination.current_page) {
          pages.push(<CurrentPageContainer aria-label={"Current page " + 1} key={i}><Text tag={TextTag.span} bold>{i}</Text></CurrentPageContainer>);
        } else {
          pages.push(<CTA key={i} tag="button" emphasis="tertiary" size="small" onClick={() => onJumpPage(i)} ariaLabel={"Page " + i}>{i}</CTA>);
        }
      }
    } else {
      pages.push(<CTA key={1} tag="button" emphasis="tertiary" size="small" onClick={() => onJumpPage(1)} ariaLabel="Page 1">1</CTA>);
      pages.push(<Text key={'trunc-end'} tag={TextTag.span}>...</Text>);
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      if (i === pagination.current_page) {
        pages.push(<CurrentPageContainer aria-label={labels.current + " " + i} key={i}><Text tag={TextTag.span} bold>{i}</Text></CurrentPageContainer>);
      } else {
        pages.push(<CTA key={i} tag="button" emphasis="tertiary" size="small" onClick={() => onJumpPage(i)} ariaLabel={"Page " + i}>{i}</CTA>);
      }
    }

    if (rangeEnd >= pagination.total_pages - 2) {
      for (let i = rangeEnd + 1; i <= pagination.total_pages; i++) {
        pages.push(<CTA key={i} tag="button" emphasis="tertiary" size="small" ariaLabel={labels.page + " " + i}>{i}</CTA>);
      }
    } else {
      pages.push(<Text key={'trunc-start'} tag={TextTag.span}>...</Text>);
      pages.push(<CTA key={pagination.total_pages} tag="button" emphasis="tertiary" size="small" onClick={() => onJumpPage(pagination.total_pages)} ariaLabel={labels.page + " " + pagination.total_pages}>{pagination.total_pages}</CTA>);
    }

    pages.push(<ButtonNext key="next" tag="button" size="small" disabled={pagination.current_page === pagination.total_pages} onClick={onNextPage} ariaLabel={labels.next}/>);
    return pages;
  }, [pagination, rangeStart, rangeEnd]);

  return (
    isLoading
    ? <PaginationSkeleton />
    : <nav aria-label={labels.navigation_label}>
      <Container gutter={ContainerGutter.dynamic0050} verticalAlign="center">
        {renderPagination()}
      </Container>
    </nav>
  );
};
